import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  orderBy,
  where,
  writeBatch,
  deleteField
} from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';
import { RoomType } from '@/shared/types';
import { calculateMinimumPrice, pricedPublishedRooms } from '@/backend/lib/residenceUtils';

/** Drop keys whose value is undefined — Firestore rejects undefined on writes. */
const stripUndefined = <T extends object>(obj: T): T =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;

/** Build an updateDoc payload where undefined values clear (delete) the field. */
const toUpdatePayload = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, v === undefined ? deleteField() : v])
  );

/** Fields every room of a (residenceId, typologyKey) group must share. */
export const TYPOLOGY_SHARED_FIELDS = [
  'name',
  'description',
  'imagesUrl',
  'floorPlanUrl',
  'area',
  'priceByOccupancy',
  'maxOccupancy',
  'minStay',
  'pricePerExcessKwh',
] as const;

type SharedField = typeof TYPOLOGY_SHARED_FIELDS[number];

/** Pick only the shared-field keys actually present in `source`. */
const pickSharedFields = (source: Partial<RoomType>): Partial<Record<SharedField, unknown>> => {
  const out: Partial<Record<SharedField, unknown>> = {};
  for (const field of TYPOLOGY_SHARED_FIELDS) {
    if (field in source) out[field] = source[field];
  }
  return out;
};

/**
 * Copy a typology's shared fields onto every sibling room so all units of a
 * (residenceId, typologyKey) group stay consistent. Returns the number synced.
 */
const propagateSharedFields = async (
  residenceRooms: RoomType[],
  typologyKey: string,
  source: Partial<RoomType>,
  excludeIds: string[]
): Promise<number> => {
  const siblings = residenceRooms.filter(
    (rt) => rt.typologyKey === typologyKey && !excludeIds.includes(rt.id)
  );
  if (siblings.length === 0) return 0;

  const shared = pickSharedFields(source);
  if (Object.keys(shared).length === 0) return 0;

  const batch = writeBatch(db);
  for (const sibling of siblings) {
    batch.update(
      doc(db, 'roomTypes', sibling.id),
      toUpdatePayload({ ...shared, updatedAt: Timestamp.now() })
    );
  }
  await batch.commit();
  return siblings.length;
};

export const useAdminRoomTypes = () => {
  return useQuery({
    queryKey: ['admin', 'roomTypes'],
    queryFn: async () => {
      const q = query(collection(db, 'roomTypes'), orderBy('name'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as RoomType[];
    }
  });
};

export const useCreateRoomType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: Omit<RoomType, 'id' | 'createdAt' | 'updatedAt'> & { id?: string; count?: number }
    ) => {
      const { id: providedId, count, ...rest } = data;
      const total = Math.max(1, count ?? 1);

      // Create `total` identical rooms. The first reuses the pre-generated id
      // (so uploaded images already reference it); the rest get auto-ids.
      const batch = writeBatch(db);
      const createdIds: string[] = [];
      for (let i = 0; i < total; i++) {
        const docRef = i === 0 && providedId
          ? doc(db, 'roomTypes', providedId)
          : doc(collection(db, 'roomTypes'));
        batch.set(docRef, stripUndefined({
          ...rest,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }));
        createdIds.push(docRef.id);
      }
      await batch.commit();

      let syncedCount = 0;

      // Sync typology siblings + recalculate the residence starting price
      if (data.residenceId) {
        const roomTypesRef = collection(db, 'roomTypes');
        const roomTypesQuery = query(roomTypesRef, where('residenceId', '==', data.residenceId));
        const snapshot = await getDocs(roomTypesQuery);
        const roomTypes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as RoomType[];

        // Propagate the shared fields onto existing siblings of the same typology.
        // Never propagate from a draft: its half-filled fields would clobber the
        // live siblings' prices/photos.
        if (rest.typologyKey && rest.status !== 'draft') {
          syncedCount = await propagateSharedFields(roomTypes, rest.typologyKey, rest, createdIds);
        }

        // Only publishable, validly-priced rooms set the public starting price;
        // drafts and price-less rooms are ignored.
        const minPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
        await updateDoc(doc(db, 'residences', data.residenceId), {
          startingPrice: minPrice,
          updatedAt: Timestamp.now()
        });
      }

      return { id: createdIds[0], syncedCount };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};

export const useUpdateRoomType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<RoomType> & { id: string }) => {
      // Get the room type to find residenceId before updating
      const roomTypeDoc = await getDoc(doc(db, 'roomTypes', id));
      let residenceId: string | undefined;
      
      if (roomTypeDoc.exists()) {
        const roomType = roomTypeDoc.data() as RoomType;
        residenceId = roomType.residenceId || data.residenceId;
      } else {
        residenceId = data.residenceId;
      }
      
      await updateDoc(doc(db, 'roomTypes', id), toUpdatePayload({
        ...data,
        updatedAt: Timestamp.now()
      }));

      let syncedCount = 0;

      // Sync typology siblings + recalculate the residence starting price
      if (residenceId) {
        const roomTypesRef = collection(db, 'roomTypes');
        const roomTypesQuery = query(roomTypesRef, where('residenceId', '==', residenceId));
        const snapshot = await getDocs(roomTypesQuery);
        const roomTypes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as RoomType[];

        // Propagate the shared fields onto siblings sharing the updated room's
        // typology. Skip when saving a draft — a half-filled draft must not
        // overwrite its published siblings' prices/photos.
        const typologyKey = roomTypes.find(rt => rt.id === id)?.typologyKey;
        if (typologyKey && data.status !== 'draft') {
          syncedCount = await propagateSharedFields(roomTypes, typologyKey, data, [id]);
        }

        // Only publishable, validly-priced rooms set the public starting price;
        // drafts and price-less rooms are ignored.
        const minPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
        await updateDoc(doc(db, 'residences', residenceId), {
          startingPrice: minPrice,
          updatedAt: Timestamp.now()
        });
      }

      return { syncedCount };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};

/**
 * Move an entire typology to a new key — rename, merge into another typology,
 * or dissolve it (toKey undefined). Applies the shared fields so every moved
 * unit stays consistent with the rest of the destination group.
 */
export const useRelabelTypology = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberIds,
      toKey,
      sharedFields,
      residenceId,
    }: {
      memberIds: string[];
      toKey?: string;
      sharedFields: Partial<RoomType>;
      residenceId?: string;
    }) => {
      if (memberIds.length === 0) return { moved: 0 };

      const payload = toUpdatePayload({
        ...pickSharedFields(sharedFields),
        typologyKey: toKey ?? undefined,
        updatedAt: Timestamp.now()
      });
      const batch = writeBatch(db);
      for (const memberId of memberIds) {
        batch.update(doc(db, 'roomTypes', memberId), payload);
      }
      await batch.commit();

      // Recalculate the residence starting price
      if (residenceId) {
        const snapshot = await getDocs(
          query(collection(db, 'roomTypes'), where('residenceId', '==', residenceId))
        );
        const roomTypes = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data()
        })) as RoomType[];
        // Only publishable, validly-priced rooms set the public starting price;
        // drafts and price-less rooms are ignored.
        const minPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
        await updateDoc(doc(db, 'residences', residenceId), {
          startingPrice: minPrice,
          updatedAt: Timestamp.now()
        });
      }

      return { moved: memberIds.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};

export const useDeleteRoomType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Get the room type to find residenceId before deleting
      const roomTypeDoc = await getDoc(doc(db, 'roomTypes', id));
      let residenceId: string | undefined;

      if (roomTypeDoc.exists()) {
        const roomType = roomTypeDoc.data() as RoomType;
        residenceId = roomType.residenceId;
      }

      await deleteDoc(doc(db, 'roomTypes', id));

      // Recalculate starting price for the residence
      if (residenceId) {
        const roomTypesRef = collection(db, 'roomTypes');
        const roomTypesQuery = query(roomTypesRef, where('residenceId', '==', residenceId));
        const snapshot = await getDocs(roomTypesQuery);
        const roomTypes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as RoomType[];

        // Only publishable, validly-priced rooms set the public starting price;
        // drafts and price-less rooms are ignored.
        const minPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
        await updateDoc(doc(db, 'residences', residenceId), {
          startingPrice: minPrice,
          updatedAt: Timestamp.now()
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};

/**
 * Delete room-type docs while protecting units that have live bookings.
 * Walks `candidateIds` in order, skips any room with a non-rejected booking,
 * and deletes booking-free rooms up to `max` (default: all of them).
 * Returns how many were removed and how many were kept because of bookings.
 */
export const useDeleteRoomTypesSafely = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      candidateIds,
      max,
      residenceId,
    }: {
      candidateIds: string[];
      max?: number;
      residenceId?: string;
    }) => {
      const limit = max ?? Infinity;
      const removableIds: string[] = [];
      let keptDueToBookings = 0;

      for (const roomId of candidateIds) {
        if (removableIds.length >= limit) break;
        const bookingSnap = await getDocs(
          query(collection(db, 'bookings'), where('roomTypeId', '==', roomId))
        );
        const hasLiveBooking = bookingSnap.docs.some(
          (d) => (d.data().status as string) !== 'rejected'
        );
        if (hasLiveBooking) {
          keptDueToBookings++;
        } else {
          removableIds.push(roomId);
        }
      }

      if (removableIds.length > 0) {
        const batch = writeBatch(db);
        for (const roomId of removableIds) {
          batch.delete(doc(db, 'roomTypes', roomId));
        }
        await batch.commit();

        // Recalculate the residence starting price
        if (residenceId) {
          const snapshot = await getDocs(
            query(collection(db, 'roomTypes'), where('residenceId', '==', residenceId))
          );
          const roomTypes = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data()
          })) as RoomType[];
          // Only publishable, validly-priced rooms set the public starting price;
        // drafts and price-less rooms are ignored.
        const minPrice = calculateMinimumPrice(pricedPublishedRooms(roomTypes));
          await updateDoc(doc(db, 'residences', residenceId), {
            startingPrice: minPrice,
            updatedAt: Timestamp.now()
          });
        }
      }

      return { removed: removableIds.length, keptDueToBookings };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};

export const useDuplicateRoomType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, count }: { id: string; count: number }) => {
      const roomTypeDoc = await getDoc(doc(db, 'roomTypes', id));
      if (!roomTypeDoc.exists()) throw new Error('Room not found');

      const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...roomTypeData } = {
        id: roomTypeDoc.id,
        ...roomTypeDoc.data()
      } as RoomType;

      for (let i = 1; i <= count; i++) {
        await addDoc(collection(db, 'roomTypes'), {
          ...roomTypeData,
          name: `${roomTypeData.name} ${i}`,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'residences'] });
      queryClient.invalidateQueries({ queryKey: ['residences'] });
    }
  });
};
